'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { rgba } from 'polished';
import { type SVGProps, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { TOPBAR_HEIGHT } from '../../atoms';
import { Badge } from '../../molecules/badge/Badge';

export type LinkTab = {
	activityIndicatorText?: string;
	disabled?: boolean;
	hasActivityIndicator?: boolean;
	href: string;
	id: string;
	testID?: string;
	text: string;
};

export type LinkTabIcon = {
	disabled?: boolean;
	icon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
	onPress?: () => void;
};

export type LinkTabBarProps = {
	backgroundColorVariant?: 'default' | 'light';
	flexVertically?: boolean;
	stickyOffsetTop?: number;
	tabs: Array<LinkTab>;
	tabsIcon?: LinkTabIcon;
	testID?: string;
	variant?: 'fixed' | 'scrollable';
};

const TABBAR_HEIGHT = 61;
const TABBAR_ZINDEX = 3;
const ICON_SIZE = 25;

/**
 * LinkTabBar component - Tab navigation component using Links
 *
 * @param tabs - Array of tab objects containing id, text, href, and optional properties
 * @param variant - Controls whether tabs are with fixed position or scrollable. Default: 'scrollable'
 * @param stickyOffsetTop - Offset from top when "fixed" variant is used. Default: TOPBAR_HEIGHT
 * @param flexVertically - Whether tabs should flex to fill available width. Default: false
 * @param backgroundColorVariant - Background color variant ('default' or 'light'). Default: 'default'
 * @param tabsIcon - Optional icon configuration with icon component and onPress handler
 * @param testID - Test identifier for the component. Default: 'link-tab-bar'
 */
export const LinkTabBar = ({
	backgroundColorVariant = 'default',
	flexVertically = false,
	stickyOffsetTop = TOPBAR_HEIGHT,
	tabs,
	tabsIcon,
	testID = 'link-tab-bar',
	variant = 'scrollable'
}: LinkTabBarProps) => {
	const { colors } = useTheme();
	const pathname = usePathname();
	const tabRefs = useRef<Array<HTMLAnchorElement | null>>([]);
	const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

	const activeTab = useMemo(() => {
		return tabs.find(tab => pathname === tab.href || pathname.startsWith(`${tab.href}/`));
	}, [pathname, tabs]);

	useEffect(() => {
		const activeIndex = activeTab ? tabs.findIndex(tab => tab.id === activeTab.id) : -1;
		const activeElement = tabRefs.current[activeIndex];

		if (activeElement) {
			const { offsetLeft, offsetWidth } = activeElement;

			setIndicatorStyle({ left: offsetLeft, width: offsetWidth });
		}
	}, [activeTab, tabs]);

	const scrollable = variant === 'scrollable';

	return (
		<S.Container $scrollable={scrollable} $stickyOffsetTop={stickyOffsetTop} data-testid={testID}>
			<S.TabsWrapper $backgroundColorVariant={backgroundColorVariant} $scrollable={scrollable}>
				{tabs.map((tab, index) => (
					<S.TabLink
						$disabled={tab.disabled}
						$flexVertically={flexVertically}
						$isActive={activeTab?.id === tab.id}
						data-testid={tab.testID || `${testID}-tab-${tab.id}`}
						href={tab.href}
						key={tab.id}
						ref={el => {
							tabRefs.current[index] = el;
						}}
					>
						{tab.text}
						{tab.hasActivityIndicator && (
							<S.BadgeWrapper>
								<Badge
									label={tab.activityIndicatorText}
									size={tab.activityIndicatorText ? 'default' : 'condensed'}
									variant="red"
								/>
							</S.BadgeWrapper>
						)}
					</S.TabLink>
				))}

				<S.Indicator style={indicatorStyle} />

				{tabsIcon?.icon && (
					<S.IconWrapper onClick={() => !tabsIcon?.disabled && tabsIcon?.onPress?.()}>
						<tabsIcon.icon
							color={tabsIcon?.disabled ? colors.text.main.enabled : colors.text.main.enabled}
							height={ICON_SIZE}
							width={ICON_SIZE}
						/>
					</S.IconWrapper>
				)}
			</S.TabsWrapper>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div<{ $scrollable: boolean; $stickyOffsetTop?: number }>`
		width: 100%;
		position: ${({ $scrollable }) => ($scrollable ? 'relative' : 'sticky')};
		top: ${({ $scrollable, $stickyOffsetTop }) => ($scrollable ? 0 : `${$stickyOffsetTop || 0}px`)};
		z-index: ${TABBAR_ZINDEX};
	`;

	export const TabsWrapper = styled.nav<{
		$backgroundColorVariant: 'default' | 'light';
		$scrollable: boolean;
	}>`
		display: flex;
		align-items: center;
		position: relative;
		overflow-x: auto;
		overflow-y: hidden;
		background-color: ${({ $backgroundColorVariant, theme }) =>
			rgba(
				$backgroundColorVariant === 'light'
					? theme.colors.backgrounds.main.container
					: theme.colors.backgrounds.main.screen,
				0.9
			)};
		backdrop-filter: ${({ $scrollable }) => ($scrollable ? 'none' : 'blur(8px)')};
		-webkit-backdrop-filter: ${({ $scrollable }) => ($scrollable ? 'none' : 'blur(8px)')};
		height: ${TABBAR_HEIGHT}px;
	`;

	export const TabLink = styled(Link)<{ $disabled?: boolean; $flexVertically?: boolean; $isActive: boolean }>`
		flex: ${({ $flexVertically }) => ($flexVertically ? '1' : 'none')};
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: ${({ theme }) => `${theme.spacings.small} ${theme.spacings.medium}`};
		white-space: ${({ $flexVertically }) => ($flexVertically ? 'nowrap' : 'auto')};
		transition: color 0.2s ease-in-out;
		cursor: pointer;
		background: transparent;

		${({ theme }) => theme.fonts.text.text1};
		color: ${({ $isActive, theme }) => {
			if ($isActive) return theme.colors.text.main.enabled;

			return theme.colors.text.main.disabled;
		}};

		${({ $disabled }) =>
			$disabled &&
			css`
				pointer-events: none;
				cursor: not-allowed;
				opacity: 0.5;
			`}

		${({ $disabled, theme }) =>
			!$disabled &&
			css`
				&:hover {
					color: ${theme.colors.text.main.enabled};
				}
			`}
	`;

	export const BadgeWrapper = styled.span`
		margin-left: ${({ theme }) => theme.spacings.xxsmall};
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const Indicator = styled.div`
		position: absolute;
		bottom: 0;
		height: 2px;
		background-color: ${({ theme }) => theme.colors.text.brand.enabled};
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	`;

	export const IconWrapper = styled.div`
		padding: ${({ theme }) => `${theme.spacings.small} ${theme.spacings.medium}`};
		margin-left: auto;
		cursor: pointer;
	`;
}
